import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight, ChevronDown, FolderClock, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LibroNotes } from "@/components/LibroNotes";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SERVICES, slugFromKey, fmtDuration } from "@/lib/services";
import { StampTrend } from "@/components/worker/StampTrend";
import { WeeklyTimeline } from "@/components/worker/WeeklyTimeline";

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

// Titolo dinamico della card registro: Servizio → Ruolo → Utente/Sede → Scuola.
// Include solo i campi realmente previsti dal flusso (dati già presenti nel registro):
// - il ruolo solo se la prestazione ne ha uno;
// - l'entità (utente o sede operativa) solo quando NON è il contesto "appalto"
//   (per l'appalto non esiste un utente/sede: niente placeholder né nome appalto come entità);
// - per gli utenti scolastici aggiunge la scuola (primo segmento del sottotitolo) per compattezza.
function composeRegisterTitle(r, cfg) {
  const isPlaceholder = (s) => !s || s === "—" || s === "\u2014";
  const parts = [cfg.label];
  if (r.role_or_task) parts.push(r.role_or_task);
  if (r.entity_kind !== "appalto") {
    if (!isPlaceholder(r.titolo)) parts.push(r.titolo);
    if (r.entity_kind === "school_user" && !isPlaceholder(r.sottotitolo)) {
      const scuola = String(r.sottotitolo).split(" · ")[0];
      if (scuola && scuola !== "\u2014") parts.push(scuola);
    }
  }
  return parts.filter(Boolean).join(" — ");
}

function StatusBadge({ status }) {
  const map = {
    archiviato: { t: "Archiviato", c: "bg-slate-200 text-slate-600" },
    riaperto_ufficio: { t: "Riaperto dall'Ufficio", c: "bg-amber-100 text-amber-700" },
    aperto: { t: "Aperto", c: "bg-success/10 text-success" },
  };
  const s = map[status] || map.archiviato;
  return <Badge className={`${s.c} hover:${s.c} border-0 text-[10px]`}>{s.t}</Badge>;
}

// Note generali del Libro Presenze (lavoratore × appalto × mese): una sola coppia in fondo al mese.
function WorkerLibroNotes({ appalto, year, month }) {
  const [notes, setNotes] = useState(null);
  useEffect(() => {
    api.get(`/libro-notes?appalto_id=${appalto.id}&year=${year}&month=${month}`).then((r) => setNotes(r.data)).catch(() => setNotes(null));
  }, [appalto.id, year, month]);
  if (!notes) return null;
  return (
    <div className="mt-6" data-testid={`worker-libro-notes-block-${appalto.id}`}>
      <h3 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><span className="inline-block h-3.5 w-1.5 rounded-full bg-[#C6A3B4]" />Note Libro Presenze – {appalto.nome}</h3>
      <LibroNotes notes={notes} editor="worker" testidPrefix={`worker-libro-notes-${appalto.id}`}
        onSave={async (text) => {
          try {
            const { data } = await api.put("/libro-notes", { appalto_id: appalto.id, year, month, note: text });
            setNotes(data); toast.success("Note lavoratore salvate");
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
        }} />
    </div>
  );
}

export default function ArchiveView() {
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openYear, setOpenYear] = useState(null);
  const [openMonth, setOpenMonth] = useState(null); // {year, month, is_current}
  const [registers, setRegisters] = useState([]);
  const [regStatus, setRegStatus] = useState("");
  const [exportAppalti, setExportAppalti] = useState([]);
  const [exporting, setExporting] = useState("");
  const [openService, setOpenService] = useState(null);

  const fmtHM = (mins) => `${Math.floor(mins / 60)} h ${String(mins % 60).padStart(2, "0")} min`;

  useEffect(() => {
    api.get("/my-months").then((r) => {
      const byYear = {};
      r.data.months.forEach((m) => {
        (byYear[m.year] = byYear[m.year] || []).push(m);
      });
      const ys = Object.keys(byYear).map(Number).sort((a, b) => b - a)
        .map((y) => ({ year: y, months: byYear[y] }));
      setYears(ys);
      if (ys[0]) setOpenYear(ys[0].year);
    }).finally(() => setLoading(false));
  }, []);

  const openRegisters = async (year, month, is_current) => {
    setOpenMonth({ year, month, is_current });
    setOpenService(null);
    const { data } = await api.get(`/archive/registers?year=${year}&month=${month}`);
    setRegisters(data.registers);
    setRegStatus(data.status);
    api.get(`/export/appalti?year=${year}&month=${month}`).then((r) => setExportAppalti(r.data || [])).catch(() => setExportAppalti([]));
  };

  const exportFile = async (kind, appaltoId, token) => {
    if (!openMonth) return;
    setExporting(token);
    try {
      let url = `/export/${kind}?year=${openMonth.year}&month=${openMonth.month}`;
      if (kind === "excel" && appaltoId) url += `&appalto_id=${appaltoId}`;
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `${kind === "excel" ? "LibroPresenze" : "Registro"}_${openMonth.year}_${openMonth.month}.${kind === "excel" ? "xlsx" : "pdf"}`;
      const objurl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objurl; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(objurl);
      toast.success(kind === "excel" ? "Libro Presenze pronto" : "Registro pronto");
    } catch (e) {
      toast.error("Esportazione non riuscita. Riprova.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => (openMonth ? setOpenMonth(null) : navigate("/lavoratore"))} data-testid="archive-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {openMonth ? "Mesi" : "Home"}
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner"><FolderClock className="h-5 w-5 text-[#22364D]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">I miei Registri</h1>
            <p className="text-sm text-slate-500">Mese corrente e mesi precedenti</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Caricamento…</p>
        ) : openMonth ? (
          <div data-testid="archive-registers">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 capitalize">{MONTHS[openMonth.month - 1]} {openMonth.year}</h2>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge status={regStatus} />
                  {openMonth.is_current && <Badge className="bg-primary/10 text-primary border-0 text-[10px]">Mese corrente</Badge>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="export-buttons">
                <Button variant="outline" size="sm" onClick={() => exportFile("pdf", null, "pdf")} disabled={!!exporting || registers.length === 0}
                  data-testid="export-pdf-btn" className="rounded-lg font-semibold">
                  <FileText className="h-4 w-4 mr-1.5 text-red-600" /> {exporting === "pdf" ? "Attendi…" : "Registro"}
                </Button>
                {exportAppalti.map((a) => {
                  const token = `excel-${a.id}`;
                  return (
                    <Button key={a.id} variant="outline" size="sm" onClick={() => exportFile("excel", a.id, token)} disabled={!!exporting}
                      data-testid={`export-libro-${a.id}`} className="rounded-lg font-semibold">
                      <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {exporting === token ? "Attendi…" : `Libro Presenze – ${a.nome}`}
                    </Button>
                  );
                })}
              </div>
            </div>
            {registers.length === 0 ? (
              <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun registro per questo mese.</CardContent></Card>
            ) : !openService ? (
              <>
                <h3 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><span className="inline-block h-3.5 w-1.5 rounded-full bg-[#C6A3B4]" />Servizi del mese</h3>
                <div className="grid grid-cols-2 gap-3" data-testid="month-service-cards">
                  {(() => {
                    const byService = {};
                    registers.forEach((r) => {
                      const g = byService[r.service_type] || (byService[r.service_type] = { service_type: r.service_type, total: 0 });
                      g.total += r.total_minutes;
                    });
                    const cards = Object.values(byService).sort((a, b) => a.service_type.localeCompare(b.service_type));
                    return cards.map((c, i) => {
                      const cfg = SERVICES[slugFromKey(c.service_type)];
                      const Icon = cfg.icon;
                      return (
                        <motion.button key={c.service_type} data-testid={`month-service-card-${c.service_type}`}
                          initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                          onClick={() => setOpenService(c.service_type)}
                          className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm transition-all duration-200 p-4 hover:shadow-md active:scale-[0.98]">
                          <div className={`h-11 w-11 rounded-xl ${cfg.accent} border border-[#C4D8E6] flex items-center justify-center mb-3`}><Icon className="h-5 w-5 text-primary" strokeWidth={2} /></div>
                          <p className="font-heading font-bold text-slate-800 leading-tight">{cfg.label}</p>
                          <p className="mt-1.5 font-heading font-bold text-primary" data-testid={`month-service-total-${c.service_type}`}>{fmtHM(c.total)}</p>
                        </motion.button>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              (() => {
                const cfg = SERVICES[slugFromKey(openService)];
                const svcRegs = registers.filter((r) => r.service_type === openService);
                const byApp = {};
                svcRegs.forEach((r) => {
                  const g = byApp[r.appalto_id] || (byApp[r.appalto_id] = { nome: r.appalto_nome || "Appalto", regs: [], sub: 0 });
                  g.regs.push(r); g.sub += r.total_minutes;
                });
                const groups = Object.values(byApp).sort((a, b) => a.nome.localeCompare(b.nome));
                const multiApp = groups.length > 1;
                const Icon = cfg.icon;
                const renderReg = (r, i) => {
                  const rc = SERVICES[slugFromKey(r.service_type)];
                  const RIcon = rc.icon;
                  return (
                    <motion.button key={`${r.service_type}-${r.entity_id}-${r.role_or_task || ""}`} data-testid={`service-register-${r.entity_id}${r.role_or_task ? `-${r.role_or_task}` : ""}`}
                      initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.03 }}
                      onClick={() => navigate(`/registro/${slugFromKey(r.service_type)}/${r.entity_id}?year=${openMonth.year}&month=${openMonth.month}${r.entity_kind ? `&kind=${r.entity_kind}` : ""}`)}
                      className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg ${rc.accent} border border-[#C4D8E6] flex items-center justify-center shrink-0`}><RIcon className="h-5 w-5 text-primary" /></div>
                      <div className="min-w-0 flex-1"><p className="font-semibold text-slate-800 truncate">{composeRegisterTitle(r, rc)}</p></div>
                      <p className="font-heading font-bold text-primary shrink-0">{fmtDuration(r.total_minutes)}</p>
                    </motion.button>
                  );
                };
                return (
                  <div data-testid="service-detail">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`h-10 w-10 rounded-lg ${cfg.accent} border border-[#C4D8E6] flex items-center justify-center shrink-0`}><Icon className="h-5 w-5 text-primary" /></div>
                      <h3 className="font-heading text-lg font-semibold text-slate-800">{cfg.label}</h3>
                    </div>
                    {multiApp ? (
                      <div className="space-y-5">
                        {groups.map((g) => (
                          <div key={g.nome} data-testid={`service-appalto-group-${g.nome}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">{g.nome}</p>
                              <p className="text-xs font-semibold text-slate-500" data-testid="appalto-subtotal">Subtotale: {fmtDuration(g.sub)}</p>
                            </div>
                            <div className="space-y-2.5">{g.regs.map(renderReg)}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2.5">{svcRegs.map(renderReg)}</div>
                    )}
                  </div>
                );
              })()
            )}
            {!openService && exportAppalti.map((a) => (
              <WorkerLibroNotes key={`${openMonth.year}-${openMonth.month}-${a.id}`} appalto={a} year={openMonth.year} month={openMonth.month} />
            ))}
          </div>
        ) : years.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="archive-empty">Nessun registro disponibile.</CardContent></Card>
        ) : (
          <div data-testid="archive-years">
            <WeeklyTimeline />
            <StampTrend />
            <div className="space-y-4 orbit-area-title">
            {years.map((y) => (
              <div key={y.year}>
                <button onClick={() => setOpenYear(openYear === y.year ? null : y.year)} data-testid={`archive-year-${y.year}`}
                  className="w-full flex items-center justify-between py-2">
                  <span className="font-heading text-lg font-bold text-slate-800">{y.year}</span>
                  {openYear === y.year ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                </button>
                {openYear === y.year && (
                  <div className="space-y-2.5">
                    {y.months.map((m) => (
                      <button key={m.month} onClick={() => openRegisters(y.year, m.month, m.is_current)} data-testid={`archive-month-${y.year}-${m.month}`}
                        className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-800 capitalize">{MONTHS[m.month - 1]}</p>
                            <StatusBadge status={m.status} />
                            {m.is_current && <Badge className="bg-primary/10 text-primary border-0 text-[10px]">Mese corrente</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">Totale: {fmtDuration(m.total_minutes)}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
